/**
 * Core Pinia implementation that doesn't import anything else
 * This breaks circular dependencies between stores and plugins
 */

import Vue from 'vue'

// Create a stores registry to prevent circular dependencies
// Use global object for both client and server side
const globalObj = typeof window !== 'undefined' ? window : global

if (!globalObj.__PINIA_STORES__) {
  globalObj.__PINIA_STORES__ = {}
}

// Mock the necessary Vue Composition API functions that Pinia needs
export const createPinia = () => ({
  install(app) {
    // Pinia will call this when installed
  },
  use() {
    // For plugins
    return this
  },
  state: {},
  _s: new Map(),
  _e: null,
})

// Simplified versions of these functions for compatibility
export const PiniaVuePlugin = {
  install(Vue) {
    Vue.mixin({
      beforeCreate() {
        const options = this.$options
        // Only define $pinia if it doesn't already exist or isn't defined as a getter
        if (!this.$pinia) {
          // Check if $pinia is defined as a getter
          const piniaDescriptor = Object.getOwnPropertyDescriptor(this, '$pinia')
          if (!piniaDescriptor || piniaDescriptor.configurable) {
            // $pinia is not defined or is configurable
            if (options.pinia) {
              Object.defineProperty(this, '$pinia', {
                value: options.pinia,
                writable: true,
                configurable: true,
              })
            } else if (options.parent && options.parent.$pinia) {
              Object.defineProperty(this, '$pinia', {
                value: options.parent.$pinia,
                writable: true,
                configurable: true,
              })
            }
          }
        }
      },
    })
  },
}

// Mock setActivePinia
export const setActivePinia = (pinia) => {
  // Store the active pinia instance
}

// Mock defineStore for simple stores
export const defineStore = (id, options) => {
  // For setup stores
  if (typeof options === 'function') {
    // Convert setup function to options format
    options = {
      state: () => ({}),
      actions: {},
      getters: {},
    }
  }

  // Check if this store is already registered
  if (globalObj.__PINIA_STORES__[id]) {
    return globalObj.__PINIA_STORES__[id]
  }

  // Create a store factory function
  const storeFactory = function () {
    // Create initial state
    const state = options.state ? options.state() : {}

    // Create a reactive state object
    const store = Vue.observable(state)

    // Add getters
    if (options.getters) {
      Object.entries(options.getters).forEach(([key, fn]) => {
        Object.defineProperty(store, key, {
          get: () => fn.call(store, store),
          enumerable: true,
        })
      })
    }

    // Add actions
    const actions = {}
    if (options.actions) {
      Object.entries(options.actions).forEach(([key, fn]) => {
        actions[key] = (...args) => fn.apply(store, args)
      })
    }

    // Return the store instance with everything combined
    return Object.assign(store, actions)
  }

  // Create a singleton factory that always returns the same instance
  const storeSingleton = function () {
    // Create the store only once
    if (!storeSingleton._instance) {
      storeSingleton._instance = storeFactory()
    }
    return storeSingleton._instance
  }

  // Register this store in our registry
  globalObj.__PINIA_STORES__[id] = storeSingleton

  return storeSingleton
}

// Initialize Pinia
Vue.use(PiniaVuePlugin)
const pinia = createPinia()
setActivePinia(pinia)

export default pinia
